/**
 * Forgot-password page. User enters their email; the server sends a
 * single-use reset link via the sendResetPassword callback wired in
 * Task 0 of #36 (packages/core/src/config/betterauth.ts).
 *
 * BetterAuth doesn't reveal whether the email exists — the success
 * screen always says "if an account exists for that email, we've sent
 * a reset link" regardless of outcome.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { authClient } from '@/shared/auth/authClient';
import { Button, Form, FormTextField } from '@/interfaces/learner/ui';
import LoadingIcon from '@/foundation/ui/LoadingIcon';
import AuthLayout from '@/shared/auth/AuthLayout';
import Logomark from '@/shared/components/Logomark';

const schema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email'),
});
type Values = z.infer<typeof schema>;

export default function ForgotPasswordPage() {
  const [sent, setSent] = useState(false);

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
    mode: 'onBlur',
  });

  const onSubmit: SubmitHandler<Values> = async ({ email }) => {
    const { error } = await authClient.requestPasswordReset({
      email,
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) {
      toast.error(error.message ?? 'Could not send reset email');
      return;
    }
    setSent(true);
  };

  if (sent) {
    return (
      <AuthLayout>
        <div className="text-center mb-8">
          <div className="mb-4 flex justify-center">
            <Logomark size={48} />
          </div>
          <h1 className="text-2xl font-bold">Check your email</h1>
        </div>
        <div className="space-y-4 text-center">
          <p className="text-sm">
            If an account exists for that email, we&apos;ve sent a reset link.
          </p>
          <div className="text-sm pt-2">
            <Link to="/login" className="text-[var(--color-accent-text)] hover:underline">
              Back to sign in
            </Link>
          </div>
        </div>
      </AuthLayout>
    );
  }

  const submitting = form.formState.isSubmitting;

  return (
    <AuthLayout>
      <div className="text-center mb-8">
        <div className="mb-4 flex justify-center">
          <Logomark size={48} />
        </div>
        <h1 className="text-2xl font-bold">Reset your password</h1>
      </div>
      <Form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormTextField
          control={form.control}
          name="email"
          label="Email"
          type="email"
          autoComplete="email"
          isRequired
          placeholder="you@example.com"
        />
        <Button type="submit" isDisabled={submitting} className="w-full">
          {submitting ? (
            <span className="flex items-center justify-center gap-2">
              <LoadingIcon size="sm" />
              Sending...
            </span>
          ) : (
            'Send reset link'
          )}
        </Button>
        <div className="text-center text-sm pt-2">
          <Link to="/login" className="text-[var(--color-accent-text)] hover:underline">
            Back to sign in
          </Link>
        </div>
      </Form>
    </AuthLayout>
  );
}
